<script setup lang="ts">
import type { IBookingService, IServiceCategory } from '@types'
import type { CalendarPeriod } from '@ui/components/calendar/picker'
import InventoryCard from '@booking/components/inventory/InventoryCard.vue'
import InventoryPeriodPicker from '@booking/components/inventory/InventoryPeriodPicker.vue'
import { useInventoryPeriodLimits } from '@booking/composables/useInventoryPeriodLimits'
import { useBookingCartStore } from '@booking/stores/bookingCart'
import { formatInventoryDuration } from '@booking/utils/inventoryDuration'
import {
  fromInventoryCalendarDate,
  getNextInventoryStart,
  isInventoryPeriodValid,
  toInventoryCalendarDate,
} from '@booking/utils/inventoryPeriod'
import { addDays, setHours, setMinutes, startOfDay } from 'date-fns'
import Search from '~icons/cue-regular/search'
import ShoppingBag from '~icons/cue-regular/shopping-bag'

const props = withDefaults(
  defineProps<{
    services?: IBookingService[]
    categories?: IServiceCategory[]
    timezone?: string
    defaultImage?: string
    layout?: 'compact' | 'sections'
    checkoutTo?: { path: string; query: Record<string, string> }
    pageSize?: number
    disabled?: boolean
    loadMoreLabel?: string
  }>(),
  {
    services: () => [],
    categories: () => [],
    timezone: '',
    defaultImage: '',
    layout: 'compact',
    checkoutTo: undefined,
    pageSize: 0,
    disabled: false,
    loadMoreLabel: '',
  },
)

const search = defineModel<string>('search', { default: '' })
const period = defineModel<CalendarPeriod>('period', { required: true })
const selectedCategory = defineModel<string | number>('categoryId', { default: '' })
const { t } = useI18n()
const bookingCart = useBookingCartStore()
const { availableInventoryItems, totalQuantity, unavailableQuantity } = storeToRefs(bookingCart)

const { now, disabledDate, toInstantPeriod, getTimeOptions } = useInventoryPeriodLimits(() => props.timezone)
const calendarPeriod = computed(() => ({
  start: toInventoryCalendarDate(period.value.start, props.timezone),
  end: toInventoryCalendarDate(period.value.end, props.timezone),
}))
const canCheckout = computed(() => isInventoryPeriodValid(period.value, now.value))
const pendingPeriod = ref<CalendarPeriod>()
const canConfirmPeriod = computed(() => !!pendingPeriod.value && isInventoryPeriodValid(pendingPeriod.value, now.value))
const showPeriodConfirmation = computed({
  get: () => !!pendingPeriod.value,
  set: (show: boolean) => {
    if (!show) {
      pendingPeriod.value = undefined
    }
  },
})
const editablePeriod = computed({
  get: () => calendarPeriod.value,
  set: (calendarValue: CalendarPeriod) => {
    const value = toInstantPeriod(calendarValue)
    if (!isInventoryPeriodValid(value)) {
      return
    }
    if (value.start.getTime() === period.value.start.getTime() && value.end.getTime() === period.value.end.getTime()) {
      return
    }

    if (totalQuantity.value && !unavailableQuantity.value) {
      pendingPeriod.value = { start: new Date(value.start), end: new Date(value.end) }
      return
    }

    period.value = value
  },
})

function confirmPeriodChange() {
  if (pendingPeriod.value && isInventoryPeriodValid(pendingPeriod.value)) {
    period.value = pendingPeriod.value
    pendingPeriod.value = undefined
  }
}

watch(period, value => bookingCart.setPeriod(value.start, value.end), { deep: true, immediate: true })
watch(
  () => props.services,
  services => bookingCart.syncInventoryServices(services),
  { immediate: true },
)

const normalizedSearch = computed(() => search.value.toLocaleLowerCase().trim())
const searchedServices = computed(() =>
  props.services.filter(
    service => !normalizedSearch.value || service.title.toLocaleLowerCase().includes(normalizedSearch.value),
  ),
)

function belongsToCategory(service: IBookingService, category: IServiceCategory) {
  return String(service.categoryId) === String(category.id) || !!category.services?.some(item => item.id === service.id)
}

const inventoryCategories = computed(() =>
  props.categories.filter(category => searchedServices.value.some(service => belongsToCategory(service, category))),
)

watch(
  inventoryCategories,
  categories => {
    if (
      selectedCategory.value !== ''
      && !categories.some(category => String(category.id) === String(selectedCategory.value))
    ) {
      selectedCategory.value = ''
    }
  },
  { immediate: true },
)

const categoryOptions = computed(() => [
  { id: '', label: t('page.services.inventory.category_all') },
  ...inventoryCategories.value.map(category => ({ id: category.id, label: category.name })),
])

const selectedCategoryData = computed(() =>
  inventoryCategories.value.find(category => String(category.id) === String(selectedCategory.value)),
)

const filteredServices = computed(() =>
  searchedServices.value.filter(service => {
    const category = selectedCategoryData.value
    return !category || belongsToCategory(service, category)
  }),
)

const startTimeOptions = computed(() => getTimeOptions(calendarPeriod.value.start))
const endTimeOptions = computed(() => getTimeOptions(calendarPeriod.value.end, calendarPeriod.value.start))

const duration = computed(() => formatInventoryDuration(period.value, t))
const visibleCount = ref(props.pageSize)
watch([search, selectedCategory, () => props.pageSize], () => {
  visibleCount.value = props.pageSize
})
const visibleServices = computed(() =>
  props.pageSize ? filteredServices.value.slice(0, visibleCount.value) : filteredServices.value,
)

function getQuantity(serviceId: number) {
  return availableInventoryItems.value.find(item => item.service.id === serviceId)?.quantity || 0
}

function setQuantity(service: IBookingService, quantity: number) {
  if (props.disabled) {
    return
  }
  bookingCart.setAvailableInventoryQuantity(service, quantity)
}

function getTime(date: Date) {
  return date.getHours() * 60 + date.getMinutes()
}

function setDatePart(source: Date, date: Date) {
  const result = new Date(date)
  result.setHours(source.getHours(), source.getMinutes(), 0, 0)
  return result
}

function setTimePart(source: Date, value: number) {
  return setMinutes(setHours(source, Math.floor(value / 60)), value % 60)
}

const startDate = computed({
  get: () => calendarPeriod.value.start,
  set: (date: Date) => {
    if (disabledDate(date)) {
      return
    }
    let start = setDatePart(calendarPeriod.value.start, date)
    if (fromInventoryCalendarDate(start, props.timezone) < new Date()) {
      start = toInventoryCalendarDate(getNextInventoryStart(props.timezone), props.timezone)
    }
    editablePeriod.value = {
      start,
      end: start >= calendarPeriod.value.end ? addDays(start, 1) : calendarPeriod.value.end,
    }
  },
})

const endDate = computed({
  get: () => calendarPeriod.value.end,
  set: (date: Date) => {
    if (disabledDate(date) || date < startOfDay(calendarPeriod.value.start)) {
      return
    }
    const end = setDatePart(calendarPeriod.value.end, date)
    editablePeriod.value = {
      start: calendarPeriod.value.start,
      end: end <= calendarPeriod.value.start ? addDays(calendarPeriod.value.start, 1) : end,
    }
  },
})

const startTime = computed({
  get: () => getTime(calendarPeriod.value.start),
  set: (value: number) => {
    const start = setTimePart(calendarPeriod.value.start, value)
    editablePeriod.value = {
      start,
      end: start >= calendarPeriod.value.end ? addDays(start, 1) : calendarPeriod.value.end,
    }
  },
})

const endTime = computed({
  get: () => getTime(calendarPeriod.value.end),
  set: (value: number) => {
    const end = setTimePart(calendarPeriod.value.end, value)
    editablePeriod.value = {
      start: calendarPeriod.value.start,
      end: end <= calendarPeriod.value.start ? addDays(end, 1) : end,
    }
  },
})
</script>

<template lang="pug">
.inventory-catalog.flex.col-24(:class="{ 'sectioned-catalog': layout === 'sections' }" :inert="disabled || undefined")
  .period-filter
    slot(name="period-heading")
    .period-heading.flex-between-center.typ-12-18-400(class="md_flex-start-stretch md_w-100 md_col-0")
      span {{ $t('page.services.inventory.period') }}
      strong.typ-12-18-500 {{ duration }}
    .mobile-only
      InventoryPeriodPicker(v-model="editablePeriod" :timezone="timezone")
    UiButton.m-t-8.mobile-only.w-100p(
      v-if="totalQuantity && checkoutTo"
      :iconLeft="ShoppingBag"
      :variant="ButtonVariant.Primary"
      :size="Sizes.Leia"
      :to="checkoutTo"
    )
      span {{ $t('page.services.inventory.checkout') }}
      UiBadge(
        :label="String(totalQuantity)"
        :variant="BadgeVariant.Accent"
        :type="BadgeDisplayType.Text"
      )
    .period-controls.desktop-only.flex.row-12.align-items-center
      .flex.row-4
        UiInputDate.w-146(v-model="startDate" data-id="inventory-start-date" :disabledDate="disabledDate" :size="Sizes.Leia" :background="Background.White" :variety="canCheckout ? undefined : MessageVariety.Error")
        UiInputSelect.w-96(v-model="startTime" data-id="inventory-start-time" maxWidth="118px" :options="startTimeOptions" :size="Sizes.Leia" :background="Background.White" :variety="canCheckout ? undefined : MessageVariety.Error")
      span.period-dash.typ-14-20-500 —
      .flex.row-4
        UiInputDate.w-146(v-model="endDate" data-id="inventory-end-date" :disabledDate="date => disabledDate(date) || date < startOfDay(calendarPeriod.start)" :size="Sizes.Leia" :background="Background.White")
        UiInputSelect.w-96(v-model="endTime" data-id="inventory-end-time" maxWidth="118px" :options="endTimeOptions" :size="Sizes.Leia" :background="Background.White")
    .cart-action.desktop-only.position-relative.flex-shrink-0(v-if="totalQuantity && canCheckout && checkoutTo")
      UiIconButton(
        data-id="inventory-checkout"
        :name="ShoppingBag"
        :variant="ButtonVariant.Primary"
        :size="Sizes.Leia"
        :to="checkoutTo"
        :aria-label="$t('page.services.inventory.cart')"
      )
      UiBadge.cart-badge.position-absolute.flex-center-center(
        :label="String(totalQuantity)"
        :variant="BadgeVariant.Accent"
        :type="BadgeDisplayType.Text"
      )
    .unavailable-notice.w-100p.p-8-12.br-12.text-center.typ-14-20-400(v-if="unavailableQuantity")
      | {{ $t('page.services.inventory.unavailable_count', { count: unavailableQuantity }) }}

  .catalog-panel.flex.col-20
    .inventory-filters.flex.row-16(v-if="inventoryCategories.length || !isLtMd || layout === 'sections'")
      .catalog-heading(v-if="$slots['catalog-heading']")
        slot(name="catalog-heading")
      UiInputSelect.category-filter.flex-1(
        v-if="inventoryCategories.length"
        v-model="selectedCategory"
        :options="categoryOptions"
        :size="Sizes.HanSolo"
        :background="Background.White"
        :bottomSheetLabel="$t('page.services.inventory.category_all')"
      )
      UiInput.inventory-search(
        v-model="search"
        :class="{ 'desktop-only': layout === 'compact' }"
        placeholder="page.services.inventory.search"
        :iconRight="Search"
        :size="Sizes.HanSolo"
        :background="Background.White"
      )
    slot(name="catalog-status")
    .catalog-content.flex.row-20
      nav.category-sidebar.flex.col-0(v-if="layout === 'sections' && inventoryCategories.length" :aria-label="$t('page.services.inventory.category_all')")
        button.category-option.typ-14-20-400(
          v-for="category of categoryOptions"
          :key="category.id"
          type="button"
          :class="{ selected: String(selectedCategory) === String(category.id) }"
          :aria-pressed="String(selectedCategory) === String(category.id)"
          @click="selectedCategory = category.id"
        ) {{ category.label }}
      .catalog-items.flex.col-20.flex-1.min-w-0
        .inventory-grid.d-grid.grid-cols-2.gap-12(class="md_grid-cols-3")
          InventoryCard(
            v-for="service of visibleServices"
            :key="service.id"
            :service="service"
            :defaultImage="defaultImage"
            :quantity="getQuantity(service.id)"
            @update:quantity="setQuantity(service, $event)"
          )
        UiButton(v-if="visibleServices.length < filteredServices.length" :label="loadMoreLabel" :variant="ButtonVariant.Ghost" :size="Sizes.Leia" @click="visibleCount += pageSize")
        slot(v-if="!filteredServices.length" name="empty")

UiPopover(
  v-model:show="showPeriodConfirmation"
  size="400px"
  title="page.services.inventory.change_period.title"
  description="page.services.inventory.change_period.description"
  cancelLabel="page.services.inventory.picker.cancel"
  confirmLabel="page.services.inventory.change_period.confirm"
  :divider="false"
  :confirmDisabled="!canConfirmPeriod"
  @confirm="confirmPeriodChange"
)
</template>

<style scoped>
.inventory-catalog {
  @screen md {
    --uno: col-20;
  }
}

.period-filter {
  position: relative;
  background: var(--cue-bg-primary-soft);
  --uno: flex col-4 p-16-12 br-14;

  @screen md {
    --uno: flex row-12 p-16 align-items-end;
    flex-wrap: wrap;
  }
}

.unavailable-notice {
  color: var(--cue-content-error-default);
  background: var(--cue-bg-surface-primary);
}

.cart-action {
  --uno: position-relative;
}

.cart-badge {
  top: -4px;
  right: -4px;
}

.period-heading,
.period-dash {
  color: var(--cue-content-neutral-secondary);

  strong {
    color: var(--cue-content-neutral-primary);
  }
}

.period-controls {
  --uno: flex-1;
}

.inventory-search {
  @screen md {
    --uno: w-247 flex-shrink-0;
  }
}

.mobile-only {
  @screen md {
    display: none;
  }
}

.desktop-only {
  display: none;

  @screen md {
    display: flex;
  }
}

.category-sidebar {
  --uno: d-none w-220 flex-shrink-0;
}

.category-option {
  --uno: text-left p-10-16 cursor-pointer;
  background: none;
  border: 0;
  border-left: 2px solid transparent;
  color: var(--cue-content-neutral-tertiary);
  &.selected {
    color: var(--cue-content-neutral-primary);
    border-left-color: var(--cue-border-primary-default);
  }
}

.sectioned-catalog {
  --uno: col-16;
  .period-filter,
  .catalog-panel {
    --uno: flex col-20 p-24-20;
    border-radius: var(--widget-radius, 20px);
    background: var(--cue-bg-neutral-primary);
    box-shadow: 6px 5px 36px color-mix(in srgb, var(--cue-content-neutral-primary) 5%, transparent);
  }
  .period-heading {
    order: 2;
    width: auto;
    --uno: flex row-8 justify-content-flex-start typ-14-20-500;
    strong {
      --uno: typ-14-20-500;
    }
  }
  .period-filter {
    --uno: align-items-stretch;
  }
  .period-controls {
    --uno: flex-none;
  }
  .inventory-filters {
    --uno: flex-wrap-wrap align-items-center;
  }
  .category-filter {
    order: 2;
    flex-basis: 100%;
  }
  .inventory-search {
    --uno: w-100p;
  }
  @screen md {
    .inventory-filters {
      --uno: row-gap-20;
    }
    .catalog-heading {
      --uno: w-100p;
    }
    .category-filter {
      --uno: flex-none w-260 o-0;
    }
    .inventory-search {
      --uno: flex-1;
      width: auto;
    }
  }
  @media (min-width: 1200px) {
    .catalog-heading {
      width: auto;
    }
    .category-filter {
      --uno: d-none;
    }
    .inventory-search {
      --uno: flex-none w-400 m-l-auto;
    }
    .category-sidebar {
      --uno: flex;
    }
    .inventory-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
  @screen lt-sm {
    .period-filter,
    .catalog-panel {
      --uno: p-16-12;
    }
    :deep(.inventory-card .ui-button) {
      --uno: p-8-6 typ-12-18-500;
    }
  }
}
</style>
